import type { ICartItemDetail } from '@/common/interfaces/cart.interface';

export const getStoreCartDesignGroupKey = (item: ICartItemDetail): string =>
  String(item.designGroupItemId ?? item.proofItemId ?? item.proofId);

export interface IStoreCartDesignGroupShell {
  cartDesign: ICartItemDetail;
  designGroupName: string;
  order: number;
}

export type StoreCartDesignGroupMap = Record<
  string,
  Array<ICartItemDetail & { originalIndex: number }>
>;

export const buildStoreCartDesignGroupMap = (
  cartItems: ICartItemDetail[]
): StoreCartDesignGroupMap =>
  cartItems.reduce<StoreCartDesignGroupMap>((acc, cartDesign, index) => {
    const groupKey = getStoreCartDesignGroupKey(cartDesign);

    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }

    acc[groupKey].push({ ...cartDesign, originalIndex: index });
    return acc;
  }, {});

/**
 * MOQ grouping — one fetch per proof. Cart lines often carry per-item designGroupItemId /
 * proofItemId values even when they belong to the same design group.
 */
export const getStoreCartMoqDesignGroupKey = (item: ICartItemDetail): string =>
  String(item.proofId ?? item.designGroupItemId ?? item.proofItemId);

export const buildStoreCartMoqDesignGroupMap = (
  cartItems: ICartItemDetail[]
): StoreCartDesignGroupMap =>
  cartItems.reduce<StoreCartDesignGroupMap>((acc, cartDesign, index) => {
    const groupKey = getStoreCartMoqDesignGroupKey(cartDesign);

    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }

    acc[groupKey].push({ ...cartDesign, originalIndex: index });
    return acc;
  }, {});

export const getSortedStoreCartDesignGroupEntries = (
  shells: Record<string, IStoreCartDesignGroupShell>
): Array<[string, IStoreCartDesignGroupShell]> =>
  Object.entries(shells).sort(
    ([, leftShell], [, rightShell]) => leftShell.order - rightShell.order
  );

export const getStoreCartDesignGroupName = (index: number): string =>
  `Design ${String.fromCharCode(65 + index)}`;

export const reindexStoreCartDesignGroupShellNames = (
  shells: Record<string, IStoreCartDesignGroupShell>
): Record<string, IStoreCartDesignGroupShell> =>
  Object.fromEntries(
    getSortedStoreCartDesignGroupEntries(shells).map(([groupKey, shell], index) => [
      groupKey,
      {
        ...shell,
        designGroupName: getStoreCartDesignGroupName(index),
      },
    ])
  );

export const upsertStoreCartDesignGroupShells = (
  shells: Record<string, IStoreCartDesignGroupShell>,
  cartItems: ICartItemDetail[],
  nextOrderStart: number
): { shells: Record<string, IStoreCartDesignGroupShell>; nextOrder: number } => {
  let nextOrder = nextOrderStart;
  const nextShells = { ...shells };

  cartItems.forEach((item) => {
    const groupKey = getStoreCartDesignGroupKey(item);
    const existingShell = nextShells[groupKey];

    if (existingShell) {
      nextShells[groupKey] = {
        ...existingShell,
        cartDesign: item,
      };
      return;
    }

    nextShells[groupKey] = {
      cartDesign: item,
      designGroupName: getStoreCartDesignGroupName(Object.keys(nextShells).length),
      order: nextOrder,
    };
    nextOrder += 1;
  });

  return { shells: nextShells, nextOrder };
};

export const mergeStoreCartDesignGroupShellsState = (
  currentShells: Record<string, IStoreCartDesignGroupShell>,
  cartItems: ICartItemDetail[],
  orderStart: number
): { shells: Record<string, IStoreCartDesignGroupShell>; nextOrder: number } => {
  const { shells, nextOrder } = upsertStoreCartDesignGroupShells(
    currentShells,
    cartItems,
    orderStart
  );

  return {
    shells: reindexStoreCartDesignGroupShellNames({ ...currentShells, ...shells }),
    nextOrder,
  };
};

export const areStoreCartDesignGroupShellsEqual = (
  left: Record<string, IStoreCartDesignGroupShell>,
  right: Record<string, IStoreCartDesignGroupShell>
): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((groupKey) => {
    const leftShell = left[groupKey];
    const rightShell = right[groupKey];

    if (!leftShell || !rightShell) {
      return false;
    }

    return (
      leftShell.order === rightShell.order &&
      leftShell.designGroupName === rightShell.designGroupName &&
      leftShell.cartDesign.id === rightShell.cartDesign.id &&
      leftShell.cartDesign.cartItemId === rightShell.cartDesign.cartItemId &&
      getStoreCartDesignGroupKey(leftShell.cartDesign) ===
        getStoreCartDesignGroupKey(rightShell.cartDesign)
    );
  });
};
